<template>
  <div
    class="d-flex flex-column gap-6"
    data-testid="appointment-details-yard-location-asset-details">
    <v-progress-circular v-if="isLoading" indeterminate color="secondary" size="15" />

    <template v-else>
      <v-divider class="my-4" v-if="assetsData.length > 0" />

      <div v-for="assetData of assetsData" :key="assetData.key" class="d-flex flex-column gap-4">
        <div>
          <p class="typography-body-sm-bold mb-3">
            {{ assetData.title }}
          </p>

          <div
            v-for="assetContainer of assetData.assetContainers"
            :key="assetContainer.id"
            class="d-flex flex-column">
            <div class="d-flex align-center gap-2">
              <v-icon size="x-small">mdi-truck-trailer</v-icon>
              <strong>{{ assetContainer.code }}</strong>
              <v-icon
                size="x-small"
                :color="getAssetContainerStatusColor(assetContainer)"
                :title="getAssetContainerStatus(assetContainer.assetContainerEvents)">
                {{ getAssetContainerStatusIcon(assetContainer) }}
              </v-icon>
            </div>
            <div class="typography-body-sm" v-if="assetVisitType !== AssetVisitType.Live">
              <span v-if="assetContainer.isAttached">{{ t('yard.messages.assetAttached') }}</span>
              <span
                v-else-if="assetContainer.spotAssignments?.length === 0"
                class="text--color-text-tertiary">
                {{ t('yard.messages.assetCurrentlyOnTheWaitingList') }}
              </span>
              <span v-else-if="assetContainer.spotAssignments?.length > 0">
                {{ assetContainer.spotAssignments[0].spot.spotArea.name }} -
                {{ assetContainer.spotAssignments[0].spot.code }}

                <v-btn
                  icon
                  @click="() => goToSpot(assetContainer.spotAssignments[0].spot)"
                  variant="text"
                  size="x-small"
                  :title="t('yard.buttons.goToSpot')">
                  <v-icon>mdi-arrow-top-right</v-icon>
                </v-btn>
              </span>
            </div>
          </div>

          <p v-if="assetData.assetContainers.length === 0" class="typography-body-sm">
            {{ t('yard.messages.noAssetLinkedToThisAppointment', { assetKey: assetData.key }) }}
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { defineComponent, onMounted, computed, ref, onBeforeMount, onBeforeUnmount } from 'vue';

import { AssetVisitType, getAssetContainerStatus } from '@nova/core';
import useYard from '@/modules/yard/composables/useYard';
import assetVisitService from '@satellite/services/asset-visit-service';
import { useEventHub, useNotify, useSockets } from '@/composables';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewAppointmentDetailsAssetDetails',
  props: {
    assetVisitType: {
      type: [String, null],
      required: true
    },
    appointmentId: {
      type: String,
      required: true
    },
    assetContainers: {
      type: Array,
      default: () => []
    },
    warehouseId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const isLoading = ref(false);
    const yardStore = useYard();
    const sockets = useSockets();
    const eventHub = useEventHub();
    const notify = useNotify();
    const { t } = useI18n();

    const dropAssetContainers = ref([]);
    const pickupAssetContainers = ref([]);

    const getAssetContainerStatusIcon = assetContainer => {
      const status = getAssetContainerStatus(assetContainer.assetContainerEvents);
      return yardStore.getAssetContainerStatusIcon(status);
    };

    const getAssetContainerStatusColor = assetContainer => {
      const status = getAssetContainerStatus(assetContainer.assetContainerEvents);
      return yardStore.getAssetContainerStatusColor(status);
    };

    const hasPickupAsset = computed(
      () =>
        props.assetVisitType === AssetVisitType.PickUp ||
        props.assetVisitType === AssetVisitType.DropHook ||
        !props.assetVisitType
    );

    const hasDropAsset = computed(
      () =>
        props.assetVisitType === AssetVisitType.Drop ||
        props.assetVisitType === AssetVisitType.DropHook ||
        !props.assetVisitType
    );

    const fetchAssetContainer = async type => {
      isLoading.value = true;
      try {
        const method =
          type === 'pickup'
            ? assetVisitService.getPickupAssetContainerByAppointmentId
            : assetVisitService.getDropAssetContainerByAppointmentId;

        const response = await method(props.appointmentId, {
          params: {
            join: [
              'assetContainerEvents',
              'spotAssignments',
              'spotAssignments.spot',
              'spotAssignments.spot.spotArea'
            ]
          }
        });

        if (!response || !Array.isArray(response)) {
          return [];
        }

        return response.map(assetContainer => ({
          ...assetContainer,
          isAttached: Boolean(props.assetContainers.find(ac => ac.id === assetContainer.id))
        }));
      } catch (error) {
        return null;
      } finally {
        isLoading.value = false;
      }
    };

    const handleYardSubspaceEvent = async event => {
      if (
        event.pickupAppointmentId === props.appointmentId ||
        event.dropAppointmentId === props.appointmentId
      ) {
        return fetchData();
      }

      assetsData.value.forEach(data => {
        data.assetContainers.forEach(ac => {
          if (ac.id === event.assetContainerId || ac.id === event.id) {
            return fetchData();
          }
        });
      });
    };

    const fetchData = async () => {
      if (hasPickupAsset.value) {
        pickupAssetContainers.value = await fetchAssetContainer('pickup');
      }

      if (hasDropAsset.value) {
        dropAssetContainers.value = await fetchAssetContainer('drop');
      }
    };

    onMounted(fetchData);

    onBeforeMount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$on(`${operation}-SpotAssignment`, handleYardSubspaceEvent);
        eventHub.$on(`${operation}-AssetContainer`, handleYardSubspaceEvent);
        eventHub.$on(`${operation}-AssetContainerEvent`, handleYardSubspaceEvent);
      });
    });

    onBeforeUnmount(() => {
      Object.keys(sockets.actions).forEach(operation => {
        eventHub.$off(`${operation}-SpotAssignment`, handleYardSubspaceEvent);
        eventHub.$off(`${operation}-AssetContainer`, handleYardSubspaceEvent);
        eventHub.$off(`${operation}-AssetContainerEvent`, handleYardSubspaceEvent);
      });
    });

    const goToSpot = async spot => {
      try {
        await yardStore.goToSpotFromOtherModules({
          spotId: spot.id,
          spotCode: spot.code,
          warehouseId: props.warehouseId
        });
      } catch (err) {
        notify(`${t('yard.messages.failedToNavigateToSpot')}: ${err}`, 'error');
        console.error(`Error navigating to spot: ${err}`);
      }
    };

    const assetsData = computed(() => {
      const data = [];

      if (props.assetVisitType === AssetVisitType.Live) {
        data.push({
          title: t('yard.messages.hookedAssets'),
          assetContainers: props.assetContainers,
          key: AssetVisitType.Live
        });
      } else if (!props.assetVisitType) {
        // No Asset visit, we only push if it has asset containers
        if (pickupAssetContainers.value.length > 0) {
          data.push({
            title: t('yard.messages.pickupAssetLocation'),
            assetContainers: pickupAssetContainers.value,
            key: AssetVisitType.PickUp
          });
        }
        if (dropAssetContainers.value.length > 0) {
          data.push({
            title: t('yard.messages.dropoffAssetLocation'),
            assetContainers: dropAssetContainers.value,
            key: AssetVisitType.Drop
          });
        }
      } else {
        // Otherwise, we push if the visitType says that it would have asset containers
        if (hasDropAsset.value) {
          data.push({
            title: t('yard.messages.dropoffAssetLocation'),
            assetContainers: dropAssetContainers.value,
            key: AssetVisitType.Drop
          });
        }
        if (hasPickupAsset.value) {
          data.push({
            title: t('yard.messages.pickupAssetLocation'),
            assetContainers: pickupAssetContainers.value,
            key: AssetVisitType.PickUp
          });
        }
      }

      return data;
    });

    return {
      getAssetContainerStatusIcon,
      getAssetContainerStatusColor,
      getAssetContainerStatus,
      goToSpot,
      AssetVisitType,
      isLoading,
      assetsData,
      t
    };
  }
});
</script>
